import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useUpdateNotes } from '../hooks/use-update-notes';

/** Editable statement note (LR-556 / T23). An untouched draft FOLLOWS fresh
 * server data (a refetch bringing another session's note re-seeds the field
 * instead of arming Save with a stale value); once the user types, the draft
 * is theirs and refetches never clobber it. Saving is then last-write-wins.
 *
 * LR-680: `canEdit` false (no `create_trades`) keeps the note VISIBLE but
 * read-only — the note is statement data, and reads stay open to every
 * authenticated user, backend included (`HasCapabilityOrReadOnly`). Required,
 * not defaulted, so a new call site has to state which it wants. With nothing
 * to read and nothing to write the block renders nothing at all: the page calls
 * this once per currency block, so an empty grey box would repeat down the
 * document as a row of inputs that look broken rather than empty. */
export const NotesEditor: React.FC<{
  statementId: number;
  savedNotes: string;
  canEdit: boolean;
  /** Keeps an unsaved draft, by statement, while the editor is unmounted. The
   * document page needs it: a block heading, and this editor with it, unmounts
   * once it scrolls out of view, and the page outlives that. */
  drafts?: Map<number, string>;
}> = ({
  statementId,
  savedNotes,
  canEdit,
  drafts,
}) => {
  const [value, setValue] = useState(
    () => drafts?.get(statementId) ?? savedNotes,
  );
  const [touched, setTouched] = useState(
    () => drafts?.has(statementId) ?? false,
  );
  // Dirty is DERIVED from the baseline comparison, not an onChange flag — an
  // edit typed back to the original re-disables Save (LR-441 class). After a
  // successful save the invalidated refetch makes savedNotes === value, so
  // dirty clears without extra state.
  const dirty = value !== savedNotes;
  const { mutate, isPending, error } = useUpdateNotes();

  useEffect(() => {
    if (!touched) setValue(savedNotes);
    // Release the draft only once the cached row has caught up with what was
    // typed — releasing on mutation success re-seeds from the still-stale
    // cache and flashes the OLD note for the length of the refetch (LR-556
    // class: invalidate-only onSuccess + draft released early).
    else if (value === savedNotes) setTouched(false);
  }, [savedNotes, touched, value]);

  useEffect(() => {
    if (touched) drafts?.set(statementId, value);
    else drafts?.delete(statementId);
  }, [drafts, statementId, touched, value]);

  // The API stores the note trimmed (DRF `CharField`), and the draft is let go
  // only once it equals what comes back - an untrimmed one never would.
  const save = () => {
    const notes = value.trim();
    setValue(notes);
    mutate({ id: statementId, notes });
  };

  // A 403 is a permissions problem — the generic "failed to save" would send
  // the user hunting for a typo. Reachable even with Save hidden: the client's
  // capability snapshot is taken once per page load (AuthGuard → /auth/me), so
  // a capability revoked server-side leaves the control on screen until the next
  // reload, and the backend stays the authority.
  const permissionDenied =
    (error as { response?: { status?: number } } | null)?.response?.status === 403;

  // After the hooks, so the early return cannot change their order.
  if (!canEdit && !savedNotes) return null;

  return (
    <div className="mt-6 border-t pt-4">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-gray-600">Note</span>
        <textarea
          value={value}
          rows={2}
          readOnly={!canEdit}
          placeholder={canEdit ? 'Optional note…' : undefined}
          onChange={(e) => {
            setValue(e.target.value);
            setTouched(true);
          }}
          className={clsx(
            'rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none',
            // The editing ring is ADDED for an editable field rather than
            // overridden for a read-only one: `focus:ring-0` would not reliably
            // beat `focus:ring-1`, since Tailwind orders those two by its own
            // rules and not by the order they appear in the attribute.
            canEdit && 'focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
            // Stays focusable so the note can be read and copied with the
            // keyboard; it just no longer lights up like "type here".
            !canEdit && 'bg-gray-50 text-gray-600',
          )}
        />
      </label>
      {error && (
        <div className="mt-1 text-xs text-red-600">
          {permissionDenied
            ? 'You do not have permission to edit statement notes.'
            : 'Failed to save the note.'}
        </div>
      )}
      {canEdit && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={!dirty || isPending}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isPending ? 'Saving…' : 'Save note'}
          </button>
        </div>
      )}
    </div>
  );
};
